import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

/**
 * iter54 — BrandingContext.
 *
 * Single source of truth for runtime branding (logo, colors, app name,
 * tagline, hero, banner, favicon, email footer).
 *
 * Fetches `/api/branding` (public, no auth) on mount + applies:
 *   - CSS variables (--brand-primary, --brand-primary-hover) on <html>
 *   - document.title
 *   - <link rel="icon"> favicon
 *
 * Provides `refresh()` for AdminSettings to call after a save.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BrandingContext = createContext({
  branding: null,
  loading: true,
  refresh: async () => {},
});

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);

  const apply = useCallback((b) => {
    if (!b) return;
    try {
      const root = document.documentElement;
      root.style.setProperty('--brand-primary', b.primary_color || '#ea580c');
      root.style.setProperty('--brand-primary-hover', b.primary_color_hover || '#c2410c');

      // Tab title
      const title = b.browser_tab_title || b.app_name || 'BOM Maestro';
      if (title) document.title = title;

      // Favicon — replace any existing <link rel="icon">
      if (b.favicon_data) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = b.favicon_data;
      }
    } catch { /* no-op */ }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/branding`);
      setBranding(res.data);
      apply(res.data);
    } catch {
      // public endpoint — if it fails, keep defaults
    } finally {
      setLoading(false);
    }
  }, [apply]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <BrandingContext.Provider value={{ branding, loading, refresh }}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => useContext(BrandingContext);
